import React, { useCallback, useEffect, useMemo, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { getSecrets } from "../../config";
import { useAppDispatch, useAppSelector } from "../../hooks";

import { addEvent } from "../../store/reducers/event";
import { getProticoClient } from "../../utils/api";
import { useWeb3Auth } from "../../hooks/useWeb3Auth";
import { useWhiteboardWebsocket } from "../../hooks/useWhiteboardWebsocket";
import { useViewport } from "../../hooks/useViewport";
interface IEventWebsocketContext {
  sendEvent: (type: string, action: string, target: string) => void;
  sendPreviewEvent: (message_id: string, url: string) => void;
  readyState: ReadyState;
}

export const EventWebsocketContext =
  React.createContext<IEventWebsocketContext>({
    sendEvent: () => {},
    sendPreviewEvent: () => {},
    readyState: ReadyState.CONNECTING,
  });

export const EventWebsocketProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  //Public API that will echo messages sent to it back to the client
  const { NODE_ENV } = getSecrets();

  const [socketUrl, setSocketUrl] = useState(null);
  const { isStale } = useViewport();
  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(socketUrl, {
    // onOpen: () => console.log("event socket opened"),
    //Will attempt to reconnect on all close events, such as server shutting down
    shouldReconnect: (closeEvent) => true,
    reconnectAttempts: 10,
    //attemptNumber will be 0 the first time it attempts to reconnect, so this equation results in a reconnect pattern of 1 second, 2 seconds, 4 seconds, 8 seconds, and then caps at 10 seconds until the maximum number of attempts is reached
    reconnectInterval: (attemptNumber) =>
      Math.min(Math.pow(2, attemptNumber) * 1000, 10000),
  });
  const { isProticoAuthenticated, address } = useWeb3Auth();
  const uuid = getProticoClient().userInfo.uuid;
  const dispatch = useAppDispatch();
  const { handleMsgPreview } = useWhiteboardWebsocket();

  useEffect(() => {
    if (!uuid) {
      return;
    }
    if (isStale) {
      setSocketUrl(null);
      return;
    }
    setSocketUrl(
      (NODE_ENV === "production" ? "wss://" : "ws://") +
        window.location.host +
        "/ws/event/" +
        uuid
    );
  }, [uuid, isStale]);

  useEffect(() => {
    if (lastMessage !== null) {
      const data = JSON.parse(lastMessage.data);
      if (data.type === "event") {
        dispatch(addEvent(data.payload));
      }
      if (data.type === "preview") {
        handleMsgPreview(data.payload);
      }
    }
  }, [lastMessage]);

  useEffect(() => {
    if (!address) return;
    getProticoClient()
      .friends.getEvents(address)
      .then((events) => {
        events.forEach((event) => dispatch(addEvent(event)));
      });
  }, [address]);
  const sendEvent = useCallback(
    (type: string, action: string, target: string) => {
      if (!address || !isProticoAuthenticated) {
        console.error("address is not isProticoAuthenticated");
        return;
      }
      sendJsonMessage({
        type: "event",
        payload: {
          address: address,
          type,
          action,
          target,
        },
      });
    },
    [address, isProticoAuthenticated]
  );
  const sendPreviewEvent = useCallback(
    (message_id: string, url: string) => {
      if (!uuid || !message_id) {
        console.error("uuid/message_id is not defined");
        return;
      }
      sendJsonMessage({
        type: "preview",
        payload: {
          uuid,
          message_id,
          url,
        },
      });
    },
    [uuid]
  );
  return (
    <EventWebsocketContext.Provider
      key={uuid}
      value={{
        sendEvent,
        sendPreviewEvent,
        readyState,
      }}
    >
      {children}
    </EventWebsocketContext.Provider>
  );
};
